import { createContext, useContext, useEffect, useRef, useState } from 'react'
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from '@firebase/auth'
import { auth } from '../firebase'
import GlobalContext from './global-context'
import { REACT_APP_BASE_URL } from '../components/utils/constant'
import { useNavigate } from 'react-router-dom'
const UserContext = createContext({})
import { BaseHTTP } from '../api/base.http'
import LogoutAlert from '../components/modules/ui/LogOut'
import {
  clearStoredBackendToken,
  clearSupportSession,
  getStoredBackendToken,
  isSupportSession,
  REGISTRATION_STATUS,
  resolveRegistrationStatus,
  storeBackendToken
} from '../utils/authSession'

const savedToken = getStoredBackendToken()

/**
 * Provides authentication context to components and hooks.
 * @typedef {Object} AuthContextValue
 * @property {Function} login - Function to log in a user with email and password.
 * @property {Function} logout - Function to log out the current user.
 * @property {import('@firebase/auth').User} user - Current authenticated user object or null.
 * @property {string|null} token - Authentication token.
 * @property {boolean} isAuthenticated - Function to set authentication token.
 * @property {Function} setIsAuthenticated - Function to set authentication token.
 * @property {boolean} isError - Function to set authentication token.
 * @property {Function} setIsError - Function to set authentication token.
 * @property {Function} setToken - Function to set authentication token.
 * @property {boolean} isLoaded - Loading state indicator.
 * @property {Function} setIsloaded - Loading state indicator.
 * @property {Function} reset - Function to reset authentication state.
 * @property {Function} setUser - Function to set authenticated user object.
 * @property {string|null} registrationStatus - Current registration progress returned by backend.
 * @property {Function} setRegistrationStatus - Function to set registration progress.
 * @property {Function} setBackendSession - Function to apply a backend session response.
 * @property {Function} refreshBackendSession - Function to rebuild the backend session from Firebase.
 * @property {Function} beginFirebaseRegistration - Function to suspend session recovery during sign-up.
 * @property {Function} endFirebaseRegistration - Function to resume session recovery after sign-up.
 */

/**
 * Provider component for authentication context.
 * @param {Object} props - React component props.
 * @param {React.ReactNode} props.children - Child components to be wrapped by the provider.
 * @returns {JSX.Element} JSX element wrapping the child components with authentication context.
 */
const ACCOUNT_DISABLED_STATUS = 403

export const AuthContextProvider = ({ children }) => {
  const {
    setBusiness,
    setCurrentBusiness,
    setCurrentBank,
    ui: { setLoader, setDialog, setDialogForm, setBlockedPeriods }
  } = useContext(GlobalContext)
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isError, setIsError] = useState(false)
  const [token, setToken] = useState(savedToken || null)
  const [registrationStatus, setRegistrationStatus] = useState(null)
  const [logoutAlertOpen, setLogoutAlertOpen] = useState(false)
  const backendLoginRequest = useRef(null)
  const firebaseRegistrationInProgress = useRef(false)
  const navigate = useNavigate()
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000 // 15 minutos en milisegundos
  let inactivityTimer

  // Conserva la identidad Firebase para poder reconstruir la sesión del backend.
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(error => {
      console.error('Error al establecer la persistencia:', error)
    })
  }, [])

  const reset = () => {
    setUser(null)
    setToken(null)
    setRegistrationStatus(null)
    setIsAuthenticated(false)
    // setIsLoaded(false)
    setIsError(false)

    // Reiniciamos los estados del GlobalContext
    setBusiness(null)
    setCurrentBusiness(undefined)
    setCurrentBank(undefined)
    // Reiniciamos la UI a sus valores predeterminados
    setLoader({ visible: false, text: '' })
    setDialog({ title: '', body: '', btnText: '', open: false })
    setDialogForm({ title: '', body: '', btnTextClose: '', btnTextOpen: '', open: false })
    setBlockedPeriods([])
  }

  const clearStoredSessionData = () => {
    clearStoredBackendToken()
    localStorage.removeItem('selectedBusiness')
    localStorage.removeItem('organizationToken')
    localStorage.removeItem('firebaseVerificationCredential')
    localStorage.removeItem('isValidated')
    BaseHTTP.getInstance().clearBackendToken()
  }

  const clearPreviousBusinessSession = () => {
    clearStoredSessionData()
    setToken(null)
    setIsAuthenticated(false)
    setBusiness(null)
    setCurrentBusiness(undefined)
    setCurrentBank(undefined)
  }

  const loginWithBackend = async firebaseToken => {
    const url = REACT_APP_BASE_URL
    // eslint-disable-next-line no-undef
    if (process.env.NODE_ENV !== 'production') {
      console.info('[Auth][backend-login]', {
        endpoint: `${url}/v1/auth/login`,
        firebaseProjectId: auth.app.options.projectId
      })
    }

    return BaseHTTP.getInstance().http.post(`${url}/v1/auth/login`, {
      token: firebaseToken
    })
  }

  const exchangeFirebaseSession = async firebaseUser => {
    if (
      backendLoginRequest.current?.uid === firebaseUser.uid &&
      backendLoginRequest.current?.promise
    ) {
      return backendLoginRequest.current.promise
    }

    const promise = firebaseUser
      .getIdToken(true)
      .then(firebaseToken => loginWithBackend(firebaseToken))
      .finally(() => {
        if (backendLoginRequest.current?.promise === promise) {
          backendLoginRequest.current = null
        }
      })

    backendLoginRequest.current = { uid: firebaseUser.uid, promise }
    return promise
  }

  const setBackendSession = (
    backendData,
    fallbackStatus = REGISTRATION_STATUS.COMPLETED,
    firebaseUser = auth.currentUser
  ) => {
    const nextRegistrationStatus = resolveRegistrationStatus(backendData, fallbackStatus)
    const nextToken = backendData?.token || null

    if (nextToken) {
      storeBackendToken(nextToken)
      BaseHTTP.getInstance().setBackendToken(nextToken)
    } else {
      clearStoredBackendToken()
      BaseHTTP.getInstance().clearBackendToken()
    }

    setToken(nextToken)
    setUser(firebaseUser || null)
    setRegistrationStatus(nextRegistrationStatus)
    setIsError(false)
    setIsAuthenticated(
      nextRegistrationStatus === REGISTRATION_STATUS.COMPLETED && Boolean(nextToken)
    )

    return {
      token: nextToken,
      registrationStatus: nextRegistrationStatus
    }
  }

  const getAuthErrorResponse = error => {
    const status = error?.response?.status || error?.status || 500
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Error inesperado.'

    return { status, message }
  }

  const refreshBackendSession = async (firebaseUser = auth.currentUser) => {
    if (!firebaseUser) {
      return { status: 401, message: 'No existe una sesión de Firebase activa.' }
    }

    try {
      const { data } = await exchangeFirebaseSession(firebaseUser)
      const backendRegistrationStatus = resolveRegistrationStatus(
        data,
        REGISTRATION_STATUS.COMPLETED
      )
      const effectiveRegistrationStatus =
        !firebaseUser.emailVerified &&
        backendRegistrationStatus === REGISTRATION_STATUS.COMPLETED
          ? REGISTRATION_STATUS.EMAIL_VERIFICATION_PENDING
          : backendRegistrationStatus
      const session = setBackendSession(
        { ...data, registrationStatus: effectiveRegistrationStatus },
        effectiveRegistrationStatus,
        firebaseUser
      )
      setIsError(false)
      setIsLoaded(true)

      return {
        status: 200,
        ...data,
        ...session
      }
    } catch (error) {
      setIsAuthenticated(false)
      setIsError(true)
      setIsLoaded(true)
      return getAuthErrorResponse(error)
    }
  }

  const beginFirebaseRegistration = () => {
    firebaseRegistrationInProgress.current = true
  }

  const endFirebaseRegistration = () => {
    firebaseRegistrationInProgress.current = false
  }

  const login = async (email, password) => {
    let userCredential

    clearPreviousBusinessSession()

    try {
      userCredential = await signInWithEmailAndPassword(auth, email, password)
      setUser(userCredential.user)
    } catch (error) {
      if (error.code === 'auth/invalid-email') {
        return { status: 400, message: 'Correo electrónico inválido.' }
      } else if (error.code === 'auth/invalid-credential') {
        return { status: 401, message: error.message }
      } else if (error.code === 'auth/user-not-found') {
        return { status: 404, message: 'Usuario no encontrado.' }
      }
      return { status: 500, message: 'Error inesperado.' }
    }

    const session = await refreshBackendSession(userCredential.user)
    if (session.status === ACCOUNT_DISABLED_STATUS) {
      await signOut(auth)
    }
    return session
  }

  const endSupportSession = () => {
    clearInactivityTimer()
    clearSupportSession()
    clearStoredSessionData()
    reset()
    navigate('/auth/signin', { replace: true })
    window.location.reload()
  }

  const restoreSupportSession = () => {
    const storedToken = getStoredBackendToken()
    if (storedToken) {
      setBackendSession(
        { token: storedToken, registrationStatus: REGISTRATION_STATUS.COMPLETED },
        REGISTRATION_STATUS.COMPLETED,
        null
      )
    }
    setIsLoaded(true)
  }

  const logout = async () => {
    if (isSupportSession()) {
      endSupportSession()
      return
    }
    try {
      setIsAuthenticated(false)
      await signOut(auth)
      setCurrentBusiness(undefined)
      setCurrentBank(undefined)
      setLoader({ visible: false, text: '' })
      setDialog({ title: '', body: '', btnText: '', open: false })
      setDialogForm({ title: '', body: '', btnTextClose: '', btnTextOpen: '', open: false })
      setBlockedPeriods([])
      clearInactivityTimer()
      navigate('/auth/signin', { replace: true })
      clearStoredSessionData()
      window.location.reload()
    } catch (error) {
      console.error('Error al cerrar sesión:', error)
    }
  }

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer)
    inactivityTimer = setTimeout(() => {
      logout()
      setLogoutAlertOpen(true)
    }, INACTIVITY_TIMEOUT)
  }

  const clearInactivityTimer = () => {
    clearTimeout(inactivityTimer)
  }

  const handleUserActivity = () => {
    if (isAuthenticated) {
      resetInactivityTimer()
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (isSupportSession()) {
        restoreSupportSession()
        return
      }
      if (firebaseUser) {
        setUser(firebaseUser)
        if (firebaseRegistrationInProgress.current) {
          setIsLoaded(true)
        } else {
          await refreshBackendSession(firebaseUser)
        }
      } else {
        reset() // limpia estados de negocio/UI
        clearStoredBackendToken()
        BaseHTTP.getInstance().clearBackendToken()
        setIsAuthenticated(false)
        setUser(null)
        setIsError(false)
        setIsLoaded(true)
      }
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'scroll', 'click']
    if (isAuthenticated) {
      events.forEach(event => window.addEventListener(event, handleUserActivity))
      resetInactivityTimer()
    }
    return () => {
      events.forEach(event => window.removeEventListener(event, handleUserActivity))
      clearInactivityTimer()
    }
  }, [isAuthenticated])

  return (
    <UserContext.Provider
      value={{
        login,
        logout,
        user,
        isAuthenticated,
        setIsAuthenticated,
        isError,
        setIsError,
        token,
        setToken,
        isLoaded,
        setIsLoaded,
        reset,
        setUser,
        registrationStatus,
        setRegistrationStatus,
        setBackendSession,
        refreshBackendSession,
        isSupportSession: isSupportSession(),
        endSupportSession,
        beginFirebaseRegistration,
        endFirebaseRegistration
      }}
    >
      {children}
      <LogoutAlert open={logoutAlertOpen} onClose={() => setLogoutAlertOpen(false)} />
    </UserContext.Provider>
  )
}

/**
 * Hook to access authentication context.
 * @returns {AuthContextValue} Authentication context values and functions.
 */
export const UserAuth = () => useContext(UserContext)
